import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAuth } from '../auth/AuthProvider'
import { PageSpinner } from '../components/ui'

/**
 * Completion page for a passwordless sign-in link (/magic?email=&token=).
 *
 * The link is normally opened on a different device from the one that asked for
 * it, so this starts its own auth session rather than expecting one to exist.
 * The token is single-use and expires after 15 minutes; both failures land on
 * the same screen with a "send me a new one" route out.
 */
export function MagicLink() {
  const [params] = useSearchParams()
  const { t } = useI18n()
  const nav = useNavigate()
  const auth = useAuth()
  const [err, setErr] = useState('')
  const ran = useRef(false) // StrictMode double-invoke would burn the token twice

  const email = params.get('email') || ''
  const token = params.get('token') || ''

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    if (!email || !token) {
      setErr(t('auth.magic.badLink'))
      return
    }
    auth
      .completeMagicLink(email, token)
      .then(() => nav('/app', { replace: true }))
      .catch(() => setErr(t('auth.magic.expired')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!err) return <PageSpinner />

  return (
    <main className="mx-auto max-w-md px-5 py-24 text-center">
      <div className="text-4xl">🔗</div>
      <h1 className="mt-3 text-2xl font-extrabold tracking-tight">{t('auth.magic.failedTitle')}</h1>
      <p className="mt-2 text-brand-600 dark:text-brand-300">{err}</p>
      <Link to="/signin" className="btn-primary mt-6">{t('auth.magic.tryAgain')}</Link>
    </main>
  )
}
